'use client';

import { useState, useEffect } from 'react';
import { Users, Globe2, TrendingUp, Eye } from 'lucide-react';

interface LiveVisitorsSectionProps {
  currentLanguage: string;
}

const LiveVisitorsSection = ({ currentLanguage }: LiveVisitorsSectionProps) => {
  const [activeVisitors, setActiveVisitors] = useState(0);
  const [todayVisitors, setTodayVisitors] = useState(0);
  const [totalVisitors, setTotalVisitors] = useState(0);
  const [recentCountries, setRecentCountries] = useState<string[]>([]);

  const content = {
    en: {
      title: "Live Website Activity",
      activeNow: "Active Now",
      todayVisits: "Today\'s Visits",
      totalVisits: "Total Visits",
      recentVisitors: "Recent Visitor Countries",
      trustBadge: "Join thousands of organizations worldwide trusting GenCarbon for their ESG journey"
    },
    hi: {
      title: "लाइव वेबसाइट गतिविधि",
      activeNow: "अभी सक्रिय",
      todayVisits: "आज की विज़िट",
      totalVisits: "कुल विज़िट",
      recentVisitors: "हाल के आगंतुक देश",
      trustBadge: "अपनी ESG यात्रा के लिए GenCarbon पर भरोसा करने वाले हजारों संगठनों में शामिल हों"
    },
    ar: {
      title: "نشاط الموقع المباشر",
      activeNow: "نشط الآن",
      todayVisits: "زيارات اليوم",
      totalVisits: "إجمالي الزيارات",
      recentVisitors: "دول الزوار الأخيرة",
      trustBadge: "انضم إلى آلاف المنظمات في جميع أنحاء العالم التي تثق في GenCarbon لرحلة ESG الخاصة بهم"
    },
    te: {
      title: "లైవ్ వెబ్‌సైట్ కార్యాచరణ",
      activeNow: "ఇప్పుడు యాక్టివ్",
      todayVisits: "నేటి సందర్శనలు",
      totalVisits: "మొత్తం సందర్శనలు",
      recentVisitors: "ఇటీవలి సందర్శకుల దేశాలు",
      trustBadge: "వారి ESG ప్రయాణం కోసం GenCarbon ను విశ్వసించే ప్రపంచవ్యాప్తంగా వేలాది సంస్థలలో చేరండి"
    }
  };

  const lang = content[currentLanguage as keyof typeof content] || content.en;

  const countries = [
    '🇮🇳 India', '🇦🇪 UAE', '🇺🇸 USA', '🇬🇧 UK', '🇩🇪 Germany', 
    '🇫🇷 France', '🇯🇵 Japan', '🇸🇬 Singapore', '🇦🇺 Australia', '🇨🇦 Canada'
  ];

  useEffect(() => {
    // Simulate real-time visitor data
    const generateVisitorData = () => {
      const baseActive = 45;
      const baseToday = 1247;
      const baseTotal = 156789;
      
      setActiveVisitors(baseActive + Math.floor(Math.random() * 20));
      setTodayVisitors(baseToday + Math.floor(Math.random() * 50));
      setTotalVisitors(baseTotal + Math.floor(Math.random() * 100));
      
      // Shuffle and pick random countries
      const shuffled = [...countries].sort(() => 0.5 - Math.random());
      setRecentCountries(shuffled.slice(0, 5));
    };

    generateVisitorData();
    const interval = setInterval(generateVisitorData, 5000);

    return () => clearInterval(interval);
  }, []);

  const stats = [
    {
      icon: Eye,
      label: lang.activeNow,
      value: activeVisitors,
      color: 'from-emerald-500 to-teal-600',
      pulse: true
    },
    {
      icon: TrendingUp,
      label: lang.todayVisits,
      value: todayVisitors.toLocaleString(),
      color: 'from-blue-500 to-cyan-600',
      pulse: false
    },
    {
      icon: Users,
      label: lang.totalVisits,
      value: totalVisitors.toLocaleString(),
      color: 'from-purple-500 to-pink-600',
      pulse: false
    }
  ];

  return (
    <section className="py-16 bg-gradient-to-br from-slate-900 via-slate-800 to-emerald-900">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            {lang.title}
          </h2>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          {stats.map((stat, index) => {
            const IconComponent = stat.icon;
            return (
              <div
                key={index}
                className="relative bg-white/10 backdrop-blur-lg rounded-2xl p-6 border border-white/20 hover:bg-white/15 transition-all duration-300"
              >
                {/* Pulse Animation for Active Visitors */}
                {stat.pulse && (
                  <div className="absolute top-4 right-4">
                    <span className="relative flex h-3 w-3">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                      <span className="relative inline-flex rounded-full h-3 w-3 bg-emerald-500"></span>
                    </span>
                  </div>
                )}

                <div className="flex items-center gap-4">
                  <div className={`p-3 rounded-xl bg-gradient-to-br ${stat.color}`}>
                    <IconComponent className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="text-white/70 text-sm mb-1">{stat.label}</p>
                    <p className="text-3xl font-bold text-white">{stat.value}</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Recent Visitor Countries */}
        <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-8 border border-white/20">
          <div className="flex items-center gap-3 mb-6">
            <Globe2 className="w-6 h-6 text-emerald-400" />
            <h3 className="text-xl font-semibold text-white">{lang.recentVisitors}</h3>
          </div>
          
          <div className="flex flex-wrap gap-4">
            {recentCountries.map((country, index) => (
              <div
                key={index}
                className="flex items-center gap-2 px-4 py-2 bg-white/10 rounded-full border border-white/20 hover:bg-white/20 transition-all duration-300"
              >
                <span className="text-2xl">{country.split(' ')[0]}</span>
                <span className="text-white font-medium">{country.split(' ').slice(1).join(' ')}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Trust Badge */}
        <div className="mt-8 text-center">
          <p className="text-white/80 text-lg max-w-3xl mx-auto">
            {lang.trustBadge}
          </p>
        </div>
      </div>
    </section>
  );
};

export default LiveVisitorsSection;